import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { toast } from 'react-toastify'
import { Alert } from '../../components/Alert'
import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { TOAST_ERROR_DEFAULT_OPTIONS, TOAST_SUCCESS_DEFAULT_OPTIONS } from '../../constants/toast'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { createResetPasswordSchema } from '../../validators/resetPasswordSchema'

type FormValues = {
  password: string
  passwordConfirmation: string
}

const invalidPasswordMessage =
  'Deve conter 8 caracteres, uma maiúscula, uma minúscula, um número e um caractere especial'
const requiredMessage = 'Campo obrigatório'
const identicalPasswordsMessage = 'Senhas devem ser idênticas'

const schema = createResetPasswordSchema({
  password: {
    min: invalidPasswordMessage,
    required: requiredMessage,
    identical: identicalPasswordsMessage,
    matches: invalidPasswordMessage,
  },
  passwordConfirmation: {
    required: requiredMessage,
    identical: identicalPasswordsMessage,
  },
})

export const RegisterPassword = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  const { signIn, signOut } = useAuth()
  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const handleRegisterPassword = async (values: FormValues) => {
    try {
      const token = searchParams.get('token')
      const response = await api.post('/auth/reset-password', {
        ...values,
        token,
      })

      await signIn({
        email: response.data.email,
        password: values.password,
      })

      toast.success(
        'Senha cadastrada com sucesso!',
        TOAST_SUCCESS_DEFAULT_OPTIONS
      )
      navigate('/painel')
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  useEffect(() => {
    signOut()
  }, [])

  return (
    <AuthLayout title="Cadastrar nova senha" withNavbar>
      {error && (
        <div className="mb-4">
          <Alert variant="error" fullWidth onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      <form
        onSubmit={handleSubmit(handleRegisterPassword)}
        className="flex flex-col justify-between h-full grow"
      >
        <section className="md:mb-14">
          <div className="mb-4">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label="Digite sua nova senha"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="passwordConfirmation"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label="Digite a senha novamente"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
        </section>

        <footer className="flex flex-col gap-2 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              fullWidth
            >
              Salvar
            </Button>
          </div>

          <div className="flex-1">
            <NavLink to={'/painel'}>
              <Button
                disabled={formState.isSubmitting}
                type="button"
                variant="outline"
                fullWidth
              >
                Cancelar
              </Button>
            </NavLink>
          </div>
        </footer>
      </form>
    </AuthLayout>
  )
}
